<template>
  <div class="centered-content">
    <div
      v-if="blankRecurringCheck"
      class="content-box"
      data-test="blank-message"
    >
      <p
        class="blank-title"
        data-test="blank-title"
      >
        You haven't scheduled any recurring donations yet.
      </p>
      <p class="paragraph">Find the causes that matter most to you on our organization search page!</p>
      <Button
        variant="outlined"
        label="Browse Organizations"
        :raised="isHovered"
        @mouseover="isHovered = true"
        @mouseleave="isHovered = false"
        :style="`color: #000`"
        @click="redirectToCMS"
      >
      </Button>
    </div>
  </div>
  <div
    v-if="donorStore.activeRecurring.length != 0"
    class="recurring-donations-header"
    data-test="active-recurring-header"
  >
    Active Recurring Donations
  </div>
  <div class="recurring-card-container">
    <div
      v-if="donorStore.activeRecurringLoading"
      class="recurring-card-container"
      data-test="loading-skeletons"
    >
      <RecurringDonationSkeleton
        v-for="index in 4"
        :key="`recurring_${index}`"
      />
    </div>

    <div
      v-for="(recurring, index) in donorStore.activeRecurring"
      :key="index"
      class="recurring-card-info"
      data-test="active-recurring-card"
    >
      <div class="recurring-card-header">
        <h3
          class="recurring-amount"
          data-test="recurring-amount"
        >
          {{ filters.formatMoney(recurring.amount_manual) }}
        </h3>
        <div class="recurring-start">
          {{ recurring.recurEveryValue }} Since {{ filters.formatDate(recurring.enteredDate) }}
        </div>
      </div>
      <div
        class="org-name"
        data-test="org-name"
      >
        {{ recurring.organizationName }}
      </div>
      <div style="display: flex; align-items: center; height: 20px">
        <img
          style="padding-right: 5px"
          :src="paymentImageSource(getProcessingInfoBrand(recurring.donorAccounts_paymentProfiles_id))"
          :alt="getProcessingInfoBrand(recurring.donorAccounts_paymentProfiles_id)"
        />{{
          getLast4(recurring.donorAccounts_paymentProfiles_id) != ''
            ? 'Ending in ' + getLast4(recurring.donorAccounts_paymentProfiles_id)
            : ''
        }}
      </div>
      <div class="button-container">
        <neon-udf
          :key="udfClosed ? Math.random() : 'udf'"
          :form-id="donorStore.formId"
          :color="donorStore.mainColor"
          :button-text-color="getContrastYIQ(donorStore.mainColor)"
          button-size="small"
          mode="prod"
          payment-processor="payrix"
          button-text="Edit"
          page="editRecurring"
          :email="donorStore.donorInfo.email"
          :donor-account-id="donorStore.donorAccounts_id"
          :donation-info="donationInfo(recurring)"
          :payment-method-options="paymentMethodOptions"
        ></neon-udf>
        <Button
          variant="outline"
          size="small"
          label="Discontinue"
          @click="openDiscontinueModal(recurring.recurringDonations_id)"
          data-test="discontinue-button"
        ></Button>
      </div>
    </div>
    <Modal
      :show="donorStore.showDeactivateRecurringDonation"
      @close="donorStore.showDeactivateRecurringDonation = false"
      data-test="discontinue-modal"
    >
      <template #header>Discontinue</template>
      <div style="width: 100%">
        <p
          v-if="donorStore.errorOccurred"
          class="error-message"
          data-test="error-message"
        >
          {{ donorStore.errorMessage }}
        </p>
        <p class="removeFavoriteTextContainer">Are you sure you would like to discontinue this recurring donation?</p>
      </div>

      <template #footer>
        <Button
          variant="link"
          label="Cancel"
          :style="`color: ${donorStore.mainColor}`"
          @click="donorStore.showDeactivateRecurringDonation = false"
          :disabled="isLoading[selectedRecurring?.value]"
        ></Button>
        <Button
          label="Discontinue"
          @click="deactivateRecurring()"
          :style="`width: 170px;color: ${getContrastYIQ(donorStore.mainColor)}`"
          :loading="isLoading[selectedRecurring?.value]"
        ></Button>
      </template>
    </Modal>
  </div>
  <div
    v-if="donorStore.inactiveRecurring.length != 0"
    class="recurring-donations-header"
    data-test="inactive-recurring-header"
  >
    Inactive Recurring Donations
  </div>
  <div class="recurring-card-container">
    <div
      v-for="(recurring, index) in donorStore.inactiveRecurring"
      :key="index"
      class="recurring-card-info"
      data-test="inactive-recurring-card"
    >
      <div class="recurring-card-header">
        <h3
          class="recurring-amount"
          data-test="recurring-amount"
        >
          {{ filters.formatMoney(recurring.amount_manual) }}
        </h3>
        <div class="recurring-start">
          {{ recurring.recurEveryValue }} Discontinued {{ filters.formatDate(recurring.updatedDate) }}
        </div>
      </div>
      <div
        class="org-name"
        data-test="org-name"
      >
        {{ recurring.organizationName }}
      </div>
      <div style="display: flex; align-items: center">
        <img
          style="padding-right: 5px"
          :src="paymentImageSource(getProcessingInfoBrand(recurring.donorAccounts_paymentProfiles_id))"
          :alt="getProcessingInfoBrand(recurring.donorAccounts_paymentProfiles_id)"
        />{{
          getLast4(recurring.donorAccounts_paymentProfiles_id) != ''
            ? 'Ending in ' + getLast4(recurring.donorAccounts_paymentProfiles_id)
            : ''
        }}
      </div>
      <div class="button-container">
        <Button
          label="Reactivate"
          size="small"
          :loading="isLoading[recurring.recurringDonations_id]"
          @click="reactivateRecurring(recurring.recurringDonations_id)"
          data-test="reactivate-button"
        ></Button>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, reactive, watch, nextTick, onMounted, computed } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import Modal from '@/components/Modal.vue';
import filters from '@/helpers/filters';
import Mastercard from '@/assets/Mastercard.svg';
import Visa from '@/assets/Visa.svg';
import Discover from '@/assets/Discover.svg';
import Amex from '@/assets/Amex.svg';
import ACH from '@/assets/ACH.svg';
import { useEventsStore } from '@/stores/EventsStore.js';
import RecurringDonationSkeleton from './Skeletons/RecurringDonationSkeleton.vue';

const isHovered = ref(false);

const donorStore = useDonorStore();
const isLoading = reactive({});

donorStore.fetchRecurringDonations();

const selectedRecurring = ref(null);
const udfClosed = ref(false);

const blankRecurringCheck = computed(() => {
  return (
    !donorStore.activeRecurringLoading &&
    donorStore.activeRecurring.length === 0 &&
    donorStore.inactiveRecurring.length === 0
  );
});

useEventsStore().subscribeToEvent('recurringPaymentUpdated', (data) => donorStore.updateRecurringPaymentMethod(data));
useEventsStore().subscribeToEvent('udfClosed', (data) => (udfClosed.value = data));

const paymentMethodOptions = ref(updatePaymentMethodOptions());

watch(
  () => donorStore.paymentProfiles,
  () => {
    paymentMethodOptions.value = updatePaymentMethodOptions();
  },
  { deep: true },
);

function updatePaymentMethodOptions() {
  let methods = donorStore.paymentProfiles.map((profile) => ({
    value: profile.donorAccounts_paymentProfiles_id,
    name: profile.profileName,
  }));
  methods.push({ name: 'New Payment Method', value: 'new_payment_method' });
  return JSON.stringify(methods);
}

const donationInfo = (recurringDonation) => {
  const info = {
    amount: recurringDonation.amount_manual,
    recurringId: recurringDonation.recurringDonations_id,
    frequency: 'recurring',
    orgId: recurringDonation.organizations_id,
    name: recurringDonation.organizationName,
    fundraiserId: recurringDonation.fundraisers_id,
    recurrence: recurringDonation.recurEveryValue.toLowerCase(),
    paymentProfileId: recurringDonation.donorAccounts_paymentProfiles_id,
  };
  return JSON.stringify(info);
};

watch(udfClosed, (newValue) => {
  if (newValue) {
    nextTick(() => {
      udfClosed.value = false;
    });
  }
});

const reactivateRecurring = async (recurringId) => {
  isLoading[recurringId] = true;
  await donorStore.updateRecurringStatus(recurringId, 0);
  isLoading[recurringId] = false;
};

const openDiscontinueModal = (id) => {
  donorStore.showDeactivateRecurringDonation = true;
  selectedRecurring.value = id;
};

const deactivateRecurring = async () => {
  isLoading[selectedRecurring.value] = true;
  await donorStore.updateRecurringStatus(selectedRecurring.value, 1);
  isLoading[selectedRecurring.value] = false;
  donorStore.showDeactivateRecurringDonation = false;
};

const getLast4 = (paymentProfileId) => {
  const matchingProfile = donorStore.paymentProfiles.find(
    (profile) => profile.donorAccounts_paymentProfiles_id === paymentProfileId,
  );
  return matchingProfile?.last4 || '';
};

const getProcessingInfoBrand = (paymentProfileId) => {
  const matchingProfile = donorStore.paymentProfiles.find(
    (profile) => profile.donorAccounts_paymentProfiles_id === paymentProfileId,
  );
  return matchingProfile?.processingInfo_brand || '';
};

const paymentImageSource = (type) => {
  switch (type) {
    case 'Visa':
      return Visa;
    case 'Checking Account':
      return ACH;
    case 'MasterCard':
      return Mastercard;
    case 'American Express':
      return Amex;
    case 'Amex':
      return Amex;
    case 'Discover':
      return Discover;
    default:
      return '';
  }
};

const redirectToCMS = () => {
  window.location.href = donorStore.domain.replace(/\/$/, '') + '/organizations';
};

onMounted(() => {
  donorStore.fetchDonorPaymentProfiles();
});
</script>
<style lang="scss" scoped>
$font-family: Poppins;
$font-color: #000;
$font-feature-settings: 'clig' off, 'liga' off;

.page {
  width: 100%;
  padding: 32px 0 48px 48px;
}

.text {
  color: $font-color;
  font-family: $font-family;
  font-style: normal;
  font-feature-settings: $font-feature-settings;
}

.recurring-donations-header {
  color: $font-color;
  font-family: $font-family;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 19.6px */
  text-transform: uppercase;
  text-align: center;
  margin: 20px 0 16px 0;
}

.recurring-amount {
  @extend .text;
  font-size: 20px;
  font-weight: 600;
  line-height: 140%; /* 28px */
  margin: 0 0 4px 0;
}

.recurring-start {
  @extend .text;
  font-size: 12px;
  font-weight: 400;
  line-height: 140%; /* 16.8px */
}

.org-name {
  @extend .text;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 400;
  line-height: 140%; /* 19.6px */
}

.recurring-card-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 12px;
}

.recurring-card-info {
  display: flex;
  flex-direction: column;
  width: 449px;
  padding: 16px 18px 24px 16px;
  border-radius: 12px;
  background: var(--White, #fff);
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
}

.recurring-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.button-container {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 16px;
}

@media screen and (max-width: 1440px) {
  .recurring-card-info {
    max-width: 680px;
  }
}

@media (max-width: 1024px) {
  .page {
    padding: 0 0 24px 0;
    align-items: center;
  }
  .recurring-card-info {
    max-width: 400px; /* Adjust this value as needed */
  }
  .recurring-card-container {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
}

@media screen and (max-width: 600px) {
  .recurring-card-info {
    display: block;
  }
}

@media screen and (max-width: 530px) {
  .recurring-card-info {
    width: 335px; /* Adjust width to account for padding */
  }
}
</style>
